import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { toast } from 'sonner';
import { ExternalLink, Plug } from 'lucide-react';
import { Page, PageHeader } from '@/components/ui/page';
import { SettingsTabs } from '@/components/settings-tabs';
import { Surface } from '@/components/ui/surface';
import {
  Section,
  SectionHeader,
  SectionTitle,
  SectionDescription,
} from '@/components/ui/section';
import { Button } from '@/components/ui/button';
import { FormField } from '@/components/ui/form-field';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Spinner } from '@/components/spinner';
import { apiGet, summarizeApiError } from '@/lib/api';
import { useProjects } from '@/features/projects/use-projects';

export function IntegrationsRoute(): JSX.Element {
  const projects = useProjects();
  const projectItems = projects.data?.items ?? [];
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as {
    google?: 'connected' | 'denied';
  };

  const [projectId, setProjectId] = useState('');
  const [starting, setStarting] = useState(false);
  const effectiveProject =
    projectId || (projectItems.length === 1 ? projectItems[0]!.id : '');

  // OAuth return leg: the API's deny redirect lands here with
  // ?google=denied. Surface it once, then strip the param.
  useEffect(() => {
    if (!search.google) return;
    if (search.google === 'connected') {
      toast.success('Google connected', {
        description: 'GA4 and Search Console access is linked.',
      });
    } else {
      toast.error('Google connection cancelled', {
        description: 'No access was granted. You can retry any time.',
      });
    }
    void navigate({ search: {} as never, replace: true });
  }, [search.google, navigate]);

  /**
   * OAuth connect is per-project: fetch the project-scoped authorize URL
   * with proper auth headers, then leave the SPA for Google. (The page used
   * to render a plain anchor to `/v1/integrations/google/authorize` — an
   * endpoint that doesn't exist and couldn't carry the Bearer token anyway,
   * so the button 404'd.) `return_to` brings the user back to this page.
   */
  const connect = async (): Promise<void> => {
    if (!effectiveProject || starting) return;
    setStarting(true);
    try {
      const returnTo = `${window.location.origin}/settings/integrations?google=connected`;
      const { url } = await apiGet<{ url: string }>(
        `/v1/projects/${effectiveProject}/integrations/google/authorize?return_to=${encodeURIComponent(returnTo)}`,
      );
      window.location.assign(url);
    } catch (e) {
      const summary = summarizeApiError(e, {
        fallbackTitle: "Couldn't start Google sign-in",
      });
      toast.error(summary.title, { description: summary.description });
      setStarting(false);
    }
  };

  return (
    <Page>
      <PageHeader
        eyebrow="Settings"
        title="Integrations"
        description="Connect Google services so reports can pull GA4 and Search Console data."
      />
      <SettingsTabs />

      <Section>
        <SectionHeader>
          <div>
            <SectionTitle className="flex items-center gap-2">
              <Plug className="h-4 w-4 text-muted-foreground" />
              Google · per project
            </SectionTitle>
            <SectionDescription>
              Google access is granted per project. Pick the project to connect
              here, or use the{' '}
              <span className="font-medium text-foreground">Integrations</span>{' '}
              tab on that project's page to also choose the GA4 property and
              Search Console site afterwards.
            </SectionDescription>
          </div>
        </SectionHeader>

        <Surface tone="muted" className="flex flex-wrap items-start gap-4 p-4">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-card border border-border/70">
            <GoogleIcon className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1 space-y-3">
            <div>
              <div className="text-sm font-semibold">Google</div>
              <div className="text-xs text-muted-foreground">
                Grant read access to GA4 and Search Console. We store refresh
                tokens encrypted at rest.
              </div>
            </div>
            {projects.isLoading ? (
              <Spinner />
            ) : projectItems.length === 0 ? (
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>Create a project first — Google access is granted per project.</p>
                <Button asChild size="sm" variant="outline">
                  <Link to="/projects">Go to Projects</Link>
                </Button>
              </div>
            ) : (
              <div className="flex flex-wrap items-end gap-3">
                {projectItems.length > 1 ? (
                  <div className="min-w-[220px]">
                    <FormField label="Project">
                      <Select
                        value={effectiveProject || undefined}
                        onValueChange={setProjectId}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Pick a project" />
                        </SelectTrigger>
                        <SelectContent>
                          {projectItems.map((p) => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormField>
                  </div>
                ) : null}
                <Button
                  onClick={() => void connect()}
                  disabled={!effectiveProject || starting}
                >
                  {starting ? <Spinner className="mr-2" /> : null}
                  Connect Google
                  <ExternalLink className="ml-1 h-3.5 w-3.5" />
                </Button>
                {effectiveProject ? (
                  <Button asChild variant="ghost" size="sm">
                    <Link
                      to="/projects/$projectId"
                      params={{ projectId: effectiveProject }}
                      hash="integrations"
                    >
                      Open project integrations
                    </Link>
                  </Button>
                ) : null}
              </div>
            )}
          </div>
        </Surface>
      </Section>
    </Page>
  );
}

function GoogleIcon({ className }: { className?: string }): JSX.Element {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      aria-hidden="true"
      fill="currentColor"
    >
      <path
        d="M21.35 11.1H12v3.2h5.35c-.23 1.45-1.7 4.25-5.35 4.25-3.22 0-5.85-2.67-5.85-5.95s2.63-5.95 5.85-5.95c1.83 0 3.06.78 3.76 1.45l2.57-2.48C16.71 4.05 14.6 3 12 3 6.92 3 2.8 7.13 2.8 12.2s4.12 9.2 9.2 9.2c5.31 0 8.83-3.72 8.83-8.96 0-.6-.07-1.05-.18-1.34Z"
        fill="#4285F4"
      />
    </svg>
  );
}
